import { useAppStoreContext } from "@/stores/appStore"
import consola from "consola"
import { useRouter } from "next-nprogress-bar"
import { useCallback, useEffect } from "react"
import { z } from "zod"

type DisallowEmpty<T> = keyof T extends never ? never : T

/**
 * Custom hook to handle embedded events received via the `message` event.
 *
 * This hook sets up an event listener for `message` events on the `window` object.
 * It validates and processes the events based on their type and payload.
 *
 * The supported event types are:
 * - `navigation`: Navigates to a specified link.
 * - `layout`: Changes the layout of the embedded page.
 * - `extensionInstalled`: Notifies that an extension has been installed.
 * - `extensionUninstalled`: Notifies that an extension has been uninstalled.
 *
 * The event data is validated using predefined schemas:
 * - `EmbeddedEventSchema` for the overall event structure.
 * - `EmbeddedEventNavigationPayloadSchema` for the navigation event payload.
 * - `EmbeddedEventLayoutPayloadSchema` for the layout event payload.
 *
 * If the event or its payload is invalid, an error is logged using `consola.error`.
 *
 * @param {Object} options - The options object.
 * @param {Object} options.callbacks - The event callbacks. The keys are the event types and the values are the callback functions.
 * @returns {void}
 * @example
 * ```tsx
 * // Code in the embedded page
 * window.parent.postMessage({
 *  from: "ringio-webphone",
 *  type: "navigation",
 *  data: {
 *    link: "/messenger",
 *  },
 * }, "*");
 * ```
 */
export function useEmbeddedEvents(
  params?: DisallowEmpty<{
    callbacks?: Partial<Record<EmbeddedEventType, (data: EmbeddedEvent) => void>>
  }>
) {
  const webphoneExpanded = useAppStoreContext((s) => s.webphoneExpanded)
  const router = useRouter()
  const setWebphoneExpanded = useAppStoreContext((s) => s.setWebphoneExpanded)

  const messageEventHandler = useCallback(
    async (event: MessageEvent) => {
      if (!event.origin.includes("ringio.com") && !event.origin.includes("ring.io")) return

      // Validate the event
      const embeddedEvent = EmbeddedEventSchema.safeParse(event.data)
      if (!embeddedEvent.success) {
        // consola.debug("Invalid embedded event", event.data)
        return
      }

      const { callbacks } = params ?? {}

      // Handle the event
      switch (embeddedEvent.data.type) {
        case "keyPressed": {
          const keyPressedPayload = EmbeddedEventKeyPressedPayloadSchema.safeParse(embeddedEvent.data.data)

          if (!keyPressedPayload.success) {
            consola.debug("Invalid key pressed payload", embeddedEvent.data.data)
            return
          }

          if (callbacks?.keyPressed) {
            callbacks.keyPressed(embeddedEvent.data)
          }

          break
        }
        case "navigation": {
          // Validate the navigation payload
          const navigationPayload = EmbeddedEventNavigationPayloadSchema.safeParse(embeddedEvent.data.data)

          if (!navigationPayload.success) {
            consola.debug("Invalid navigation payload", embeddedEvent.data.data)
            return
          }

          // Navigate to the link
          router.push(navigationPayload.data.link)

          if (callbacks?.navigation) {
            callbacks.navigation(embeddedEvent.data)
          }

          break
        }
        case "layout": {
          // Validate the layout payload
          const layoutPayload = EmbeddedEventLayoutPayloadSchema.safeParse(embeddedEvent.data.data)

          if (!layoutPayload.success) {
            consola.debug("Invalid layout payload", embeddedEvent.data.data)
            return
          }

          // Handle the layout change
          switch (layoutPayload.data.webPhoneStatus) {
            case "expanded":
              setWebphoneExpanded(true)
              break
            case "collapsed":
              setWebphoneExpanded(false)
              break
            default:
              consola.debug("Unknown webphone status", layoutPayload.data.webPhoneStatus)
              break
          }

          if (callbacks?.layout) {
            callbacks.layout(embeddedEvent.data)
          }

          break
        }
        case "notification": {
          const payload = EmbeddedEventNotificationPayloadSchema.safeParse(embeddedEvent.data.data)

          if (!payload.success) {
            consola.warn("Invalid notification payload", embeddedEvent.data.data)
            return
          }

          if (!("Notification" in window)) {
            consola.warn("Notification API not available")
            return
          }

          let hasPermission = false

          if (Notification.permission !== "granted" && Notification.permission !== "denied") {
            const permission = await Notification.requestPermission()
            if (permission !== "granted") {
              consola.warn("Notification permission denied")
              return
            }

            hasPermission = true
          } else if (Notification.permission === "granted") {
            hasPermission = true
          }

          if (!hasPermission) {
            return
          }

          const notification = new Notification(payload.data.title, {
            icon: payload.data.icon,
            body: payload.data.body,
            badge: payload.data.badge,
            data: payload.data.data,
            dir: payload.data.dir,
            lang: payload.data.lang,
            requireInteraction: payload.data.requireInteraction,
            silent: payload.data.silent,
            tag: payload.data.tag,
          })
          notification.onclick = () => window.focus()
        }
        default:
          consola.debug("Unknown embedded event type", embeddedEvent.data.type)
          break
      }
    },
    [params, router, setWebphoneExpanded]
  )

  useEffect(() => {
    window.addEventListener("message", messageEventHandler)

    return () => {
      window.removeEventListener("message", messageEventHandler)
    }
  }, [messageEventHandler])
}

export const EmbeddedEventType = z.enum(["navigation", "layout", "notification", "keyPressed"])
export type EmbeddedEventType = z.infer<typeof EmbeddedEventType>

export const EmbeddedEventSchema = z.object({
  from: z.string(),
  type: EmbeddedEventType,
  data: z.any(),
})
export type EmbeddedEvent = z.infer<typeof EmbeddedEventSchema>

export const EmbeddedEventNavigationPayloadSchema = z.object({
  link: z.string(), // Must be a relative path
})
export type EmbeddedEventNavigationPayload = z.infer<typeof EmbeddedEventNavigationPayloadSchema>

export const EmbeddedEventLayoutPayloadSchema = z.object({
  webPhoneStatus: z.enum(["expanded", "collapsed"]),
})
export type EmbeddedEventLayoutPayload = z.infer<typeof EmbeddedEventLayoutPayloadSchema>

export const EmbeddedEventNotificationPayloadSchema = z.object({
  title: z.string(),
  icon: z.string().optional(),
  body: z.string().optional(),
  badge: z.string().optional(),
  data: z.any().optional(),
  dir: z.enum(["auto", "ltr", "rtl"]).optional(),
  lang: z.string().optional(),
  requireInteraction: z.boolean().optional(),
  silent: z.boolean().nullish(),
  tag: z.string().optional(),
})

export const ShortcutType = z.enum(["mod + K", "mod + P"])
export type ShortcutType = z.infer<typeof ShortcutType>

export const EmbeddedEventKeyPressedPayloadSchema = ShortcutType

export type EmbeddedEventKeyPressedPayload = z.infer<typeof EmbeddedEventKeyPressedPayloadSchema>
